import './index.scss'
import Loader from "react-loaders";
import AnimatedLetters from "../AnimatedLetters";
import { useEffect, useState, useRef } from "react";
import emailjs from '@emailjs/browser'
import { MapContainer, Popup, TileLayer, Marker } from 'react-leaflet';
import { trackFormSubmit } from '../../utils/analytics';

const Contact = () => {

    const [letterClass, setLetterClass] = useState('text-animate')
    const [formStatus, setFormStatus] = useState('idle') // idle, sending, success, error
    const [formErrors, setFormErrors] = useState({})
    const refForm = useRef()

    useEffect(() => {
        const timerId = setTimeout(() => {
            setLetterClass('text-animate-hover');
        }, 3000);

        return () => {
            clearTimeout(timerId);
        };
    }, []);

    const validateForm = () => {
        const errors = {}
        const formData = new FormData(refForm.current)

        const name = formData.get('name')?.trim()
        const email = formData.get('email')?.trim()
        const subject = formData.get('subject')?.trim()
        const message = formData.get('message')?.trim()

        if (!name || name.length < 2) {
            errors.name = 'Name must be at least 2 characters'
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!email || !emailRegex.test(email)) {
            errors.email = 'Please enter a valid email address'
        }

        if (!subject || subject.length < 5) {
            errors.subject = 'Subject must be at least 5 characters'
        }

        if (!message || message.length < 10) {
            errors.message = 'Message must be at least 10 characters'
        }

        setFormErrors(errors)
        return Object.keys(errors).length === 0
    }

    const sendEmail = async (e) => {
        e.preventDefault()

        if (!validateForm()) {
            return
        }

        setFormStatus('sending')
        setFormErrors({})

        // Track form submission
        const formData = {
            name: refForm.current.name?.value || '',
            email: refForm.current.email?.value || '',
            subject: refForm.current.subject?.value || '',
            hasMessage: !!refForm.current.message?.value,
        };
        trackFormSubmit('contact_form', formData);

        try {
            await emailjs.sendForm(
                'service_kr2tbsb',
                'template_lz3jh1d',
                refForm.current,
                'F71zpKqApi0Izm3eQ'
            )

            setFormStatus('success')
            setTimeout(() => {
                setFormStatus('idle')
                refForm.current.reset()
            }, 3000)
        } catch (error) {
            console.error('Failed to send the message:', error);
            setFormStatus('error')
            setTimeout(() => {
                setFormStatus('idle')
            }, 3000)
        }
    }

    return (
        <>
            <div className="container contact-page">
                <div className="text-zone">
                    <h1>
                        <AnimatedLetters
                            letterClass={letterClass}
                            strArray={['C', 'o', 'n', 't', 'a', 'c', 't', ' ', 'm', 'e']}
                            idx={15}
                        />
                    </h1>
                    <p>
                        Currently, I am actively engaged in exploring freelance opportunities, particularly those that stimulate my creativity and offer avenues for innovative problem-solving. I am excited by the prospect of collaborating on projects that challenge conventional thinking and inspire fresh perspectives.
                    </p>
                    <p>
                        Should you have any inquiries or project proposals, I warmly encourage you to utilize the contact form provided below. Your questions and requests are welcomed, and I am eager to explore how my skills and expertise can contribute to the success of your endeavors.
                    </p>

                    <div className={`contact-form ${formStatus}`}>
                        {formStatus === 'success' && (
                            <div className="status-message success">
                                <div className="status-icon">✓</div>
                                <div className="status-text">
                                    <h3>Message Sent Successfully!</h3>
                                    <p>Thank you for reaching out. I'll get back to you soon.</p>
                                </div>
                            </div>
                        )}

                        {formStatus === 'error' && (
                            <div className="status-message error">
                                <div className="status-icon">✕</div>
                                <div className="status-text">
                                    <h3>Failed to Send Message</h3>
                                    <p>Please try again or contact me directly via email.</p>
                                </div>
                            </div>
                        )}

                        <form ref={refForm} onSubmit={sendEmail}>
                            <ul>
                                <li className="half">
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            name="name"
                                            placeholder=" "
                                            required
                                            className={formErrors.name ? 'error' : ''}
                                        />
                                        <label htmlFor="name">Full Name *</label>
                                        <span className="input-highlight"></span>
                                        {formErrors.name && <span className="error-text">{formErrors.name}</span>}
                                    </div>
                                </li>
                                <li className="half">
                                    <div className="input-group">
                                        <input
                                            type="email"
                                            name="email"
                                            placeholder=" "
                                            required
                                            className={formErrors.email ? 'error' : ''}
                                        />
                                        <label htmlFor="email">Email Address *</label>
                                        <span className="input-highlight"></span>
                                        {formErrors.email && <span className="error-text">{formErrors.email}</span>}
                                    </div>
                                </li>
                                <li>
                                    <div className="input-group">
                                        <input
                                            placeholder=" "
                                            type="text"
                                            name="subject"
                                            required
                                            className={formErrors.subject ? 'error' : ''}
                                        />
                                        <label htmlFor="subject">Subject *</label>
                                        <span className="input-highlight"></span>
                                        {formErrors.subject && <span className="error-text">{formErrors.subject}</span>}
                                    </div>
                                </li>
                                <li>
                                    <div className="input-group">
                                        <textarea
                                            placeholder=" "
                                            name="message"
                                            required
                                            className={formErrors.message ? 'error' : ''}
                                        ></textarea>
                                        <label htmlFor="message">Message *</label>
                                        <span className="input-highlight"></span>
                                        {formErrors.message && <span className="error-text">{formErrors.message}</span>}
                                    </div>
                                </li>
                                <li>
                                    <button
                                        type="submit"
                                        className={`submit-button ${formStatus}`}
                                        disabled={formStatus === 'sending'}
                                    >
                                        <span className="button-text">
                                            {formStatus === 'sending' ? 'Sending...' : 'Send Message'}
                                        </span>
                                        <div className="button-loader"></div>
                                        <span className="button-icon">
                                            {formStatus === 'sending' ? '⏳' : '→'}
                                        </span>
                                    </button>
                                </li>
                            </ul>
                        </form>
                    </div>
                </div>

                <div className='info-map'>
                    Nabil Alhaffar
                    <br />
                    N Swall Dr
                    <br />
                    West Hollywood, CA 90048<br />
                    <span>Nabil.Alhaffar1@gmail.com</span>
                </div>
                <div className='map-wrap'>
                    <MapContainer center={[34.07596, -118.38545]} zoom={12}>
                        <TileLayer 
                            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                        />
                        <Marker position={[34.07596, -118.38545]}>
                            <Popup>Nabil lives here, come over for a cup of coffee :-</Popup>
                        </Marker>
                    </MapContainer>
                </div>
            </div>
            <Loader type="pacman" />
        </>
    )
}

export default Contact;